import type {
    ClientPositionView,
    SavedDestination
} from "@solomon-apps/shared";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import {
    CheckIcon,
    CrossIcon,
    PencilPaperIcon,
    TrashIcon
} from "@solomon-labs/design/components/icons";
import {
    Address,
    Badge,
    ButtonIcon,
    Combobox,
    ComboboxCollection,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxInput,
    ComboboxItem,
    ComboboxList,
    InputGroupAddon,
    StatusBadge
} from "@solomon-labs/design/components/ui";
import React, { useEffect, useMemo, useRef, useState } from "react";

import { DisabledReason } from "@/components/DisabledReason";

import {
    isValidSolanaAddress,
    looksLikeExchangeAddress
} from "../../shared/format";
import type { RewardsDestinationChoice } from "../../shared/RewardsDestinationPicker";

/** One pickable destination: the position's own wallet, a saved one, or a
 *  new address added in this edit. */
interface RoutingOption {
    key: string;
    label: string;
    address: string;
}

/** The "+Add New Address" row, last in both lists. */
const ADD_KEY = "add-new-address";
const ADD_OPTION: RoutingOption = {
    key: ADD_KEY,
    label: "+Add New Address",
    address: "+Add New Address"
};
/** A new address added in this edit: not saved anywhere yet. */
const NEW_PREFIX = "new:";
const UNLABELED = "Unlabeled";

/** Label | Address | Split (| Actions in edit mode), shared by both rows. */
const ROUTING_GRID =
    "grid grid-cols-[minmax(0,1.2fr)_minmax(0,2fr)_4rem] items-center gap-4";
const EDIT_GRID =
    "grid grid-cols-[minmax(0,1.2fr)_minmax(0,2fr)_4rem_4.5rem] items-center gap-4";

/** A destination is routed whole today: one destination takes every reward. */
const ONLY_SPLIT = "100%";
const ONLY_DESTINATION_REASON = "Only Destination";

/** The position's own wallet first, then every saved destination once. */
function routingOptions(
    position: ClientPositionView,
    destinations: SavedDestination[]
): RoutingOption[] {
    const source = position.account.address;
    const sourceLabel = position.account.label?.trim();
    const options: RoutingOption[] = [
        {
            key: source,
            label:
                sourceLabel && sourceLabel !== source
                    ? sourceLabel
                    : "Position Wallet",
            address: source
        }
    ];
    for (const destination of destinations) {
        if (options.some((option) => option.address === destination.address))
            continue;
        const name = destination.name.trim();
        options.push({
            key: destination.address,
            label: name && name !== destination.address ? name : UNLABELED,
            address: destination.address
        });
    }
    return options;
}

/**
 * The position's Rewards Routing: one row per destination (Label, Address,
 * Split). Read-only until the routing is in edit mode (`editable`, once the
 * position's ownership is verified): then an Actions column appears, and Edit
 * (pencil) turns the row's Label and Address into two linked comboboxes over
 * the position's wallet and the saved destinations: picking a label picks its
 * address and the other way round.
 *
 * Both lists end with +Add New Address, and both stay comboboxes:
 * - Address: the typed address becomes the pick, a new entry in the list
 *   (Invalid Address when it is not one).
 * - Label: what was typed is kept as the new address's label, and the
 *   Address field takes focus for the address itself.
 * While a new address is picked, typing in Label names it.
 *
 * Check keeps the row's change (`onStage`), Cross drops it; the change is
 * saved, through the page's signed flow, from the section's Save. Trash is
 * there for when a holding routes to several destinations; with one
 * destination taking every reward there is nothing to remove it for.
 */
export function RewardsRoutingTable({
    position,
    destinations,
    currentAddress,
    currentLabel,
    staged,
    editable,
    rowEditing,
    allowUnchanged,
    onEditRow,
    onStage,
    onDiscardRow,
    addRoute = null
}: {
    position: ClientPositionView;
    destinations: SavedDestination[];
    currentAddress: string | null;
    currentLabel: string;
    /** A change kept from the row's editor, not saved yet. */
    staged: RewardsDestinationChoice | null;
    /** Edit mode: the Actions column, and the row can be edited. */
    editable: boolean;
    /** The row's comboboxes are open. */
    rowEditing: boolean;
    /** Check may keep the current destination again (re-verify). */
    allowUnchanged: boolean;
    onEditRow: () => void;
    onStage: (choice: RewardsDestinationChoice) => void;
    onDiscardRow: () => void;
    /** A last row under the routes (the +Add Route action), when given. */
    addRoute?: React.ReactNode;
}): React.ReactElement {
    const editing = editable && rowEditing;
    const onConfirm = onStage;
    const onDiscard = onDiscardRow;
    // The row starts from the kept change, else from what is routed now.
    const startAddress = staged?.address ?? currentAddress;
    const options = useMemo(
        () => routingOptions(position, destinations),
        [position, destinations]
    );

    // The pick both comboboxes share (an option's key), the new address
    // added in this edit (if any), its label, and why an add was refused.
    const [pickedKey, setPickedKey] = useState<string>(startAddress ?? "");
    const [added, setAdded] = useState<string | null>(null);
    const [newLabel, setNewLabel] = useState("");
    const [addProblem, setAddProblem] = useState<string | null>(null);
    const addressFieldRef = useRef<HTMLDivElement>(null);

    // Each opening of the editor starts from what is routed now.
    useEffect(() => {
        if (!editing) return;
        const keptNew =
            staged?.isSavedNew &&
            !options.some((option) => option.address === staged.address);
        setPickedKey(
            keptNew ? `${NEW_PREFIX}${staged.address}` : (startAddress ?? "")
        );
        setAdded(keptNew ? staged.address : null);
        setNewLabel(keptNew ? (staged.label ?? "") : "");
        setAddProblem(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editing]);

    // `added` is "" while a new entry has its label but not its address yet.
    const addedOption: RoutingOption | null =
        added !== null
            ? { key: `${NEW_PREFIX}${added}`, label: newLabel, address: added }
            : null;
    const items = [
        ...options,
        ...(addedOption ? [addedOption] : []),
        ADD_OPTION
    ];
    const picked =
        items.find(
            (option) => option.key === pickedKey && option.key !== ADD_KEY
        ) ?? null;
    const pickedIsNew = picked?.key.startsWith(NEW_PREFIX) ?? false;

    const pick = (next: RoutingOption | null): void => {
        if (!next || next.key === ADD_KEY) return;
        setAddProblem(null);
        setPickedKey(next.key);
    };
    /** +Add New Address from the Address list: the typed text is the address. */
    const addFromAddress = (typed: string): void => {
        const address = typed.trim();
        if (!isValidSolanaAddress(address)) {
            setAddProblem(address ? "Invalid Address" : "Enter an Address");
            return;
        }
        const existing = options.find((option) => option.address === address);
        if (existing) {
            setAddProblem(null);
            setPickedKey(existing.key);
            return;
        }
        setAddProblem(null);
        setAdded(address);
        setPickedKey(`${NEW_PREFIX}${address}`);
    };
    /**
     * +Add New Address from the Label list: a new entry starts here, under
     * the typed label, with its address still to come (the Address field
     * takes focus). A label that already names a destination picks it.
     */
    const addFromLabel = (typed: string): void => {
        const label = typed.trim();
        const existing = label
            ? options.find((option) => option.label === label)
            : undefined;
        if (existing) {
            setAddProblem(null);
            setPickedKey(existing.key);
            return;
        }
        const address = added ?? "";
        setNewLabel(label);
        setAdded(address);
        setPickedKey(`${NEW_PREFIX}${address}`);
        // After the Label list has closed and handed its focus back.
        window.setTimeout(
            () => addressFieldRef.current?.querySelector("input")?.focus(),
            0
        );
    };

    const exchange =
        pickedIsNew && picked?.address
            ? looksLikeExchangeAddress(picked.address)
            : false;
    // An address left in the field that could not be added blocks the save:
    // the pick behind it is not what the field shows.
    const saveBlocked = addProblem
        ? addProblem
        : !picked
          ? "Pick a Destination"
          : pickedIsNew && !picked.address
            ? "Enter an Address"
            : picked.address === currentAddress &&
                !allowUnchanged &&
                !pickedIsNew
              ? "No Change"
              : null;

    const save = (): void => {
        if (saveBlocked || !picked) return;
        if (pickedIsNew) {
            onConfirm({
                address: picked.address,
                label: newLabel.trim() || undefined,
                isSavedNew: true
            });
            return;
        }
        onConfirm({
            address: picked.address,
            label: picked.label === UNLABELED ? undefined : picked.label,
            isSavedNew: false
        });
    };

    const grid = editable ? EDIT_GRID : ROUTING_GRID;
    const headings = editable
        ? ["Label", "Address", "Split", "Actions"]
        : ["Label", "Address", "Split"];
    // What the row reads: the kept change, else what is routed now.
    const shownAddress = staged?.address ?? currentAddress;
    const shownLabel = staged
        ? staged.label ||
          options.find((option) => option.address === staged.address)?.label ||
          "New Address"
        : currentLabel;

    return (
        // The +Add Route row closes the table itself: no rule under it.
        <div
            className={`-mx-4 md:-mx-6 ${addRoute ? "" : "border-b border-line-base"}`}
        >
            <div
                className={`${grid} border-b border-line-base px-4 py-1 md:px-6 md:py-3`}
            >
                {headings.map((heading) => (
                    <Text
                        key={heading}
                        variant="label"
                        render={<span />}
                        className="text-foreground-muted"
                    >
                        {heading}
                    </Text>
                ))}
            </div>
            <div className={`${grid} px-4 py-1 md:px-6 md:py-3`}>
                {editing ? (
                    <>
                        <RoutingCombobox
                            ariaLabel="Label"
                            items={items}
                            picked={picked}
                            text={(option) => option.label}
                            onPick={pick}
                            onAdd={addFromLabel}
                            // A new address's label is whatever is typed.
                            freeText={pickedIsNew ? setNewLabel : undefined}
                            placeholder={
                                pickedIsNew ? "Label (optional)" : undefined
                            }
                        />
                        <div className="flex min-w-0 items-center gap-2">
                            <div
                                ref={addressFieldRef}
                                className="min-w-0 flex-1"
                            >
                                <RoutingCombobox
                                    ariaLabel="Address"
                                    items={items}
                                    picked={picked}
                                    text={(option) => option.address}
                                    onPick={pick}
                                    onAdd={addFromAddress}
                                    onTyped={() => setAddProblem(null)}
                                    placeholder="Solana address"
                                    invalid={addProblem !== null}
                                    numeric
                                    // What is wrong (or worth a second
                                    // look) sits in the field's line, left
                                    // of the chevron.
                                    endBadge={
                                        addProblem ? (
                                            <Badge variant="fill" color="error">
                                                {addProblem}
                                            </Badge>
                                        ) : exchange ? (
                                            <Badge
                                                variant="fill"
                                                color="warning"
                                            >
                                                Exchange Address
                                            </Badge>
                                        ) : null
                                    }
                                />
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        <span className="flex min-w-0 items-center gap-3">
                            <Text
                                variant="body"
                                render={<span />}
                                className="truncate text-foreground-base"
                            >
                                {shownLabel}
                            </Text>
                            {staged ? (
                                <StatusBadge variant="plain" tone="pending">
                                    Unsaved
                                </StatusBadge>
                            ) : null}
                        </span>
                        {shownAddress ? (
                            <Address
                                mono={false}
                                value={shownAddress}
                                className="text-foreground-muted"
                            />
                        ) : (
                            <Text
                                variant="body"
                                render={<span />}
                                className="text-foreground-muted"
                            >
                                Not set
                            </Text>
                        )}
                    </>
                )}
                <Text
                    variant="number"
                    render={<span />}
                    className="tabular-nums text-foreground-base"
                >
                    {ONLY_SPLIT}
                </Text>
                {editable ? (
                    <span className="flex items-center gap-2">
                        {editing ? (
                            <>
                                <DisabledReason
                                    reason={saveBlocked}
                                    label="Keep Rewards Destination"
                                >
                                    <ButtonIcon
                                        variant="transparent"
                                        size="lg"
                                        icon={<CheckIcon />}
                                        aria-label="Keep Rewards Destination"
                                        disabled={saveBlocked !== null}
                                        onClick={save}
                                    />
                                </DisabledReason>
                                <ButtonIcon
                                    variant="transparent"
                                    size="lg"
                                    icon={<CrossIcon />}
                                    aria-label="Cancel Editing Rewards Destination"
                                    onClick={onDiscard}
                                />
                            </>
                        ) : (
                            <>
                                <ButtonIcon
                                    variant="transparent"
                                    size="lg"
                                    icon={<PencilPaperIcon />}
                                    aria-label="Edit Rewards Destination"
                                    onClick={onEditRow}
                                />
                                <DisabledReason
                                    reason={ONLY_DESTINATION_REASON}
                                    label="Remove Rewards Destination"
                                >
                                    <ButtonIcon
                                        variant="transparent"
                                        size="lg"
                                        icon={<TrashIcon />}
                                        aria-label="Remove Rewards Destination"
                                        disabled
                                    />
                                </DisabledReason>
                            </>
                        )}
                    </span>
                ) : null}
            </div>
            {addRoute ? (
                <div className="border-t border-line-base px-4 py-1 md:px-6">
                    {addRoute}
                </div>
            ) : null}
        </div>
    );
}

/**
 * One of the row's two linked comboboxes: the same options, each showing
 * one side (its label, or its address). The text follows the shared pick,
 * so choosing in one fills the other; typing filters on that side, and a
 * closed list goes back to the pick. +Add New Address hands up what was
 * typed. With `freeText`, what is typed is kept (a new address's label).
 */
function RoutingCombobox({
    ariaLabel,
    items,
    picked,
    text,
    onPick,
    onAdd,
    onTyped,
    freeText,
    placeholder,
    invalid = false,
    numeric = false,
    endBadge = null
}: {
    ariaLabel: string;
    items: RoutingOption[];
    picked: RoutingOption | null;
    text: (option: RoutingOption) => string;
    onPick: (option: RoutingOption | null) => void;
    onAdd: (typed: string) => void;
    onTyped?: () => void;
    freeText?: (typed: string) => void;
    placeholder?: string;
    invalid?: boolean;
    numeric?: boolean;
    /** A badge inside the field, between its text and the chevron. */
    endBadge?: React.ReactNode;
}): React.ReactElement {
    const shown = picked ? text(picked) : "";
    const [query, setQuery] = useState(shown);
    // What the user last typed (not the text a pick writes back), which
    // +Add New Address adds.
    const typedRef = useRef("");
    // A pick since the last keystroke: closing then is not an add.
    const pickedRef = useRef(false);
    // Text left in the field by a click-away that could not be added (an
    // invalid address): it stays until the next keystroke or pick, whatever
    // the list resets its input to.
    const heldRef = useRef<string | null>(null);
    // The text follows the pick, except while it holds an address that
    // could not be added (`invalid`): that stays through the other field's
    // changes (a new label starting its entry, say) until it is fixed,
    // typed over, or a destination is picked.
    useEffect(() => {
        if (heldRef.current !== null && invalid) return;
        // Mid-typing in this field: what is typed wins until the list closes.
        if (typedRef.current) return;
        heldRef.current = null;
        setQuery(shown);
    }, [shown, invalid]);

    return (
        <Combobox<RoutingOption>
            items={items}
            value={picked}
            onValueChange={(next: RoutingOption | null) => {
                pickedRef.current = true;
                heldRef.current = null;
                if (next?.key === ADD_KEY) {
                    onAdd(typedRef.current);
                    return;
                }
                onPick(next);
            }}
            inputValue={query}
            onInputValueChange={(next: string, details) => {
                const typing = details.reason === "input-change";
                if (!typing && heldRef.current !== null) {
                    setQuery(heldRef.current);
                    return;
                }
                // Picking the add row writes its own text: never the field's.
                if (!typing && next === ADD_OPTION.label) return;
                // A new address's label is what was typed: only typing or a
                // pick replaces it (not Escape, not closing the list).
                if (freeText && !typing && details.reason !== "item-press") {
                    return;
                }
                setQuery(next);
                // Only real typing counts; a pick also rewrites the text.
                if (!typing) return;
                typedRef.current = next;
                pickedRef.current = false;
                heldRef.current = null;
                onTyped?.();
                freeText?.(next);
            }}
            onOpenChange={(open: boolean, details) => {
                if (open) return;
                const typed = typedRef.current;
                typedRef.current = "";
                // Typing and clicking away is the same as +Add New Address:
                // the text is what they meant, so it stays (an invalid
                // address stays too, with its problem). Escape is the one
                // way back to what was there; a new address's label
                // (freeText) already keeps what was typed.
                if (
                    !freeText &&
                    !pickedRef.current &&
                    details.reason !== "escape-key" &&
                    typed.trim() &&
                    typed !== shown
                ) {
                    // Held until it is added or replaced; a successful add
                    // moves the pick, and the new text follows it.
                    heldRef.current = typed;
                    // The list may already have reset the text; put it back.
                    setQuery(typed);
                    onAdd(typed);
                    return;
                }
                // The list reports its close more than once: a held entry
                // stays put through the repeats.
                if (!freeText && heldRef.current === null) setQuery(shown);
            }}
            isItemEqualToValue={(a, b) => a.key === b.key}
            itemToStringLabel={text}
            // An untouched field lists everything; typing narrows it. The
            // add row is always offered.
            filter={(option, search) =>
                option.key === ADD_KEY ||
                search === shown ||
                text(option).toLowerCase().includes(search.trim().toLowerCase())
            }
            autoHighlight
        >
            <ComboboxInput
                aria-label={ariaLabel}
                size="sm"
                className="w-full"
                showClear={false}
                placeholder={placeholder}
                state={invalid ? "error" : undefined}
                inputClassName={
                    numeric ? "font-numeric tabular-nums" : undefined
                }
            >
                {endBadge ? (
                    // The DS sorts every inline-end addon last, the chevron
                    // first in the DOM; order-1 puts the badge before it.
                    // pr-0 -mr-2 takes its own padding out and pulls it 0.5rem
                    // into the chevron addon's (pl 0.75rem + 0.25rem icon
                    // inset): 0.5rem, gap-2, from badge to chevron.
                    <InputGroupAddon
                        align="inline-end"
                        className="order-1 -mr-2 pr-0"
                    >
                        {endBadge}
                    </InputGroupAddon>
                ) : null}
            </ComboboxInput>
            <ComboboxContent>
                <ComboboxList>
                    <ComboboxEmpty>No match.</ComboboxEmpty>
                    <ComboboxCollection>
                        {(option: RoutingOption) => (
                            <ComboboxItem key={option.key} value={option}>
                                {option.key === ADD_KEY ? (
                                    option.label
                                ) : (
                                    <span className="flex min-w-0 flex-col">
                                        <span className="truncate">
                                            {option.label || "New address"}
                                        </span>
                                        {option.address ? (
                                            <Address
                                                mono={false}
                                                value={option.address}
                                                textVariant="fine-print"
                                                className="text-foreground-muted"
                                            />
                                        ) : null}
                                    </span>
                                )}
                            </ComboboxItem>
                        )}
                    </ComboboxCollection>
                </ComboboxList>
            </ComboboxContent>
        </Combobox>
    );
}
